import { type I18n, setupI18n } from "@lingui/core";
import * as Sentry from "@sentry/tanstackstart-react";
import { MutationCache, notifyManager, QueryCache, QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { routerWithQueryClient } from "@tanstack/react-router-with-query";
import { getGlobalStartContext } from "@tanstack/react-start";
import {
	createTRPCClient,
	httpBatchLink,
	httpLink,
	isNonJsonSerializable,
	splitLink,
} from "@trpc/client";
import { createTRPCOptionsProxy, type TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import type { PropsWithChildren } from "react";
import { toast } from "sonner";
import { ErrorComponent } from "~/components/error-component";
import { NotFound } from "~/components/not-found";
import { PendingComponent } from "~/components/pending-component";
import { AudioPlayerProvider } from "~/contexts/audio-player-context";
import { getHeaders } from "~/functions/headers";
import { getOrigin } from "~/functions/origin";
import { parseError } from "~/lib/errors";
import { routerWithLingui } from "~/modules/lingui/plugin";
import { routeTree } from "~/routeTree.gen";
import { TRPCProvider } from "~/trpc/client";
import type { TRPCRouter } from "~/trpc/router";
import { PlaybackProvider } from "./hooks/use-playback";

export interface AppContext {
	i18n: I18n;
	queryClient: QueryClient;
	trpc: TRPCOptionsProxy<TRPCRouter>;
}

export function getRouter() {
	const context = getGlobalStartContext();
	const i18n = context?.i18n ?? setupI18n();
	const origin = getOrigin();

	const trpcClient = createTRPCClient<TRPCRouter>({
		links: [
			splitLink({
				condition: (op) => isNonJsonSerializable(op.input),
				// Use httpLink for FormData
				true: httpLink({
					url: `${origin}/api/trpc`,
					headers: getHeaders(),
				}),
				false: httpBatchLink({
					url: `${origin}/api/trpc`,
					headers: getHeaders(),
				}),
			}),
		],
	});

	const queryClient: QueryClient = new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnReconnect: () => !queryClient.isMutating(),
				retry: false,
				staleTime: 60_000,
			},
		},
		mutationCache: new MutationCache({
			onError: async (error, variables, _onMutateResult, mutation, _context) => {
				const { message, code } = parseError(error);

				if (code === 401 && !mutation.options.mutationKey?.includes("auth")) {
					try {
						const refreshSessionMutation = await trpcClient.auth.refresh.mutate();
						if (refreshSessionMutation) {
							toast.error(i18n._("Your session has expired. Please retry."), {
								action: {
									label: i18n._("Retry"),
									onClick: () => {
										mutation.execute(variables);
									},
								},
							});
						}
					} catch {
						toast.error(i18n._("Your session has expired. Please sign in again."));

						queryClient.clear();
						await router.navigate({
							to: "/auth/login",
							replace: true,
							search: { redirect: router.latestLocation.pathname },
						});
					}
				} else {
					toast.error(message);
				}
			},
			async onSettled() {
				if (queryClient.isMutating() === 1) {
					// Automatically refresh queries after a mutation
					return queryClient.invalidateQueries();
				}
			},
		}),
		queryCache: new QueryCache({
			onError: async (error) => {
				const { message, code } = parseError(error);

				if (code === 401) {
					try {
						const refreshSessionMutation = await trpcClient.auth.refresh.mutate();
						if (refreshSessionMutation) {
							queryClient.invalidateQueries();
						}
					} catch {
						toast.error(i18n._("Your session has expired. Please sign in again."));
						queryClient.clear();
						await router.navigate({
							to: "/auth/login",
							replace: true,
							search: { redirect: router.latestLocation.pathname },
						});
					}
				} else {
					toast.error(message, {
						action: {
							label: i18n._("Retry"),
							onClick: () => {
								queryClient.invalidateQueries();
							},
						},
					});
				}
			},
		}),
	});

	if (typeof document !== "undefined") {
		notifyManager.setScheduler(window.requestAnimationFrame);
	}

	const router = routerWithLingui(
		routerWithQueryClient(
			createTanStackRouter({
				routeTree,
				context: {
					i18n,
					queryClient,
					trpc: createTRPCOptionsProxy({
						client: trpcClient,
						queryClient: queryClient,
					}),
				},
				defaultPreload: "intent",
				defaultViewTransition: true,
				defaultErrorComponent: ErrorComponent,
				defaultNotFoundComponent: () => <NotFound />,
				defaultPendingComponent: PendingComponent,
				defaultStaleTime: 60 * 1000,
				defaultPreloadStaleTime: 60 * 1000,
				scrollRestoration: true,
				Wrap: ({ children }: PropsWithChildren) => {
					return (
						<TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
							<AudioPlayerProvider>
								<PlaybackProvider>{children}</PlaybackProvider>
							</AudioPlayerProvider>
						</TRPCProvider>
					);
				},
			}),
			queryClient,
		),
		i18n,
	);

	if (!router.isServer) {
		Sentry.init({
			dsn: import.meta.env.VITE_SENTRY_DSN,
			tunnel: "/tunnel",
			environment: import.meta.env.VITE_ENVIRONMENT || "local",
			release: import.meta.env.VITE_VERSION,
			enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",

			ignoreErrors: ["Skipped ViewTransition", "Hydration failed"],

			integrations: [
				Sentry.tanstackRouterBrowserTracingIntegration(router),
				Sentry.browserProfilingIntegration(),
			],

			// Set tracesSampleRate to 1.0 to capture 100%
			// of transactions for tracing.
			tracesSampleRate: 1.0,

			// Set profilesSampleRate to 1.0 to profile every transaction.
			// Since profilesSampleRate is relative to tracesSampleRate,
			// the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
			// For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
			// results in 25% of transactions being profiled (0.5*0.5=0.25)
			profilesSampleRate: 1.0,

			// Setting this option to true will send default PII data to Sentry.
			// For example, automatic IP address collection on events
			sendDefaultPii: true,
		});
	}

	return router;
}

type AppRouter = ReturnType<typeof getRouter>;

declare module "@tanstack/react-router" {
	interface Register {
		router: AppRouter;
	}
}
